import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowDown, Mail, MessageCircle } from "lucide-react";
const rodrigoPhoto = "/rodrigo-haertel.webp";
import Navbar from "@/components/Navbar";
import StatsBar from "@/components/StatsBar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Timeline from "@/components/Timeline";
import Fronts from "@/components/Fronts";
import { trackMetaEvent } from "@/lib/metaCapi";
import { WhatsAppLink } from "@/components/WhatsAppLink";

const Index = () => {
  const photoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackMetaEvent({ eventName: "ViewContent" });
  }, []);

  // Subtle parallax on hero photo (disabled with prefers-reduced-motion)
  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = photoRef.current;
        if (!el) return;
        const y = Math.max(-40, Math.min(40, window.scrollY * -0.06));
        el.style.transform = `translate3d(0, ${y}px, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main id="main" className="min-h-screen bg-background text-foreground">
      <a href="#main" className="skip-link">Pular para o conteúdo</a>
      <Navbar />

      {/* HERO */}
      <section
        id="home"
        className="relative overflow-hidden px-6 pb-24 pt-36 md:pb-32 md:pt-44 lg:pt-52"
      >
        <div className="grain" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-16 md:grid-cols-[1.3fr_1fr] md:gap-20">
          <div className="animate-fade-up">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-border/70 bg-background-warm/60 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-pulse-dot rounded-full bg-accent/70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              Disponível para mentorias 2026
            </div>

            <h1 className="text-balance font-serif text-5xl font-light leading-[1.02] tracking-tight text-foreground sm:text-6xl md:text-7xl lg:text-[5.25rem]">
              Negócios se constroem com{" "}
              <span className="italic text-accent">visão, método</span> e
              comportamento empreendedor.
            </h1>

            <div className="mt-10 max-w-xl space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              <p>
                Atuo no desenvolvimento de negócios e empreendedores, com foco em{" "}
                <span className="font-medium text-foreground">
                  estratégia empresarial, comportamento empreendedor e decisão
                  estratégica aplicada à gestão.
                </span>
              </p>
              <p>
                Meu trabalho conecta estratégia, comportamento e resultado — da
                clareza sobre o negócio à aplicação prática no dia a dia do
                empreendedor.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-accent px-7 py-6 text-base text-accent-foreground hover:bg-accent/90"
              >
                <a href="#frentes">
                  Conheça minhas frentes
                  <ArrowDown className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full border-border/70 bg-transparent px-7 py-6 text-base text-foreground hover:bg-background-warm"
              >
                <a href="#contato">Falar comigo</a>
              </Button>
            </div>
          </div>

          <div ref={photoRef} className="relative mx-auto w-full max-w-sm will-change-transform md:max-w-none">
            <div className="absolute -inset-6 rounded-full bg-accent/10 blur-3xl" />
            <div className="relative aspect-square overflow-hidden rounded-full border border-accent/30 shadow-[0_20px_80px_-20px_hsl(var(--accent)/0.35)] transition-transform duration-700 hover:scale-[1.02]">
              <img
                src={rodrigoPhoto}
                alt="Rodrigo Haertel — mentor empresarial e metodologista do Empretec"
                width={800}
                height={800}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <StatsBar />

      {/* QUEM É */}
      <section id="quem-sou" className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 flex items-center gap-4">
            <span className="h-px w-10 bg-accent" />
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Trajetória
            </span>
          </div>
          <h2 className="max-w-3xl font-serif text-4xl font-light leading-[1.05] text-foreground sm:text-5xl md:text-6xl">
            Quem é <span className="italic text-accent">Rodrigo Haertel</span>
          </h2>

          <Timeline />
        </div>
      </section>

      {/* FRENTES DE ATUAÇÃO */}
      <section id="frentes" className="bg-background-warm px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 flex items-center gap-4">
            <span className="h-px w-10 bg-accent" />
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Frentes de atuação
            </span>
          </div>

          <Fronts />
        </div>
      </section>

      {/* CONTATO */}
      <section id="contato" className="relative overflow-hidden px-6 py-24 md:py-32">
        <div className="grain" />
        <div className="relative mx-auto max-w-4xl text-center">
          <div className="mb-8 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-accent" />
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Contato
            </span>
            <span className="h-px w-10 bg-accent" />
          </div>

          <h2 className="font-serif text-4xl font-light leading-tight text-foreground sm:text-5xl md:text-6xl">
            Vamos construir algo <span className="italic text-accent">sólido</span>.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
            Consultor empresarial, mentor de negócios e metodologista do Empretec.
            Atuação em todo o Brasil.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              asChild
              variant="outline"
              className="rounded-full border-accent/40 bg-transparent px-6 text-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <WhatsAppLink
                source="hero"
                message="Olá Rodrigo, gostaria de conversar."
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Conversar no WhatsApp
              </WhatsAppLink>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-accent/40 bg-transparent px-6 text-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <Link to="/contato">
                <Mail className="mr-2 h-4 w-4" />
                Enviar mensagem
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Index;
